import { MOSIDA_API_BASE_URL } from "../config/apiUrls";

const apiBase = MOSIDA_API_BASE_URL;

export const SELECTED_FIFA_CATEGORY_STORAGE_KEY = "selectedFifaCategory";

export interface FifaCategory {
  id: string;
  name: string;
  prompt: string | null;
  status: boolean;
  is_stats?: boolean;
  reference_image_url?: string | null;
  icon: string | null;
  icon_url: string | null;
  slider_images?: string[] | null;
  slider_image_urls?: string[] | null;
  used_count: number;
  created_at: string;
  updated_at: string;
}

function getHeaders(): HeadersInit {
  return {
    accept: "application/json",
  };
}

export function fifaCategoryIconUrl(category: Partial<FifaCategory>): string {
  return category.icon_url?.trim() || category.icon?.trim() || "";
}

export function fifaCategorySliderImageUrls(
  category: Partial<FifaCategory>
): string[] {
  const urls =
    category.slider_image_urls?.length
      ? category.slider_image_urls
      : category.slider_images;

  return (urls || []).filter(
    (url): url is string => typeof url === "string" && url.trim().length > 0
  );
}

export function persistSelectedFifaCategory(category: FifaCategory): void {
  try {
    localStorage.setItem(SELECTED_FIFA_CATEGORY_STORAGE_KEY, JSON.stringify(category));
  } catch {
    /* ignore storage errors */
  }
}

export function readSelectedFifaCategory(): FifaCategory | null {
  try {
    const raw = localStorage.getItem(SELECTED_FIFA_CATEGORY_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as FifaCategory;
  } catch {
    return null;
  }
}

export async function getActiveFifaCategories(): Promise<{
  success: boolean;
  data: FifaCategory[];
  error?: string;
  message?: string;
}> {
  try {
    const response = await fetch(`${apiBase}/fifa-categories/active`, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        data: [],
        error: `Failed to load FIFA categories: ${response.status} ${text}`,
      };
    }

    const json = await response.json();
    const data = Array.isArray(json?.data) ? json.data : [];
    return {
      success: Boolean(json?.success !== false),
      data,
      message: json?.message,
      error: json?.success === false ? json?.message : undefined,
    };
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : "Network error while loading FIFA categories";
    return {
      success: false,
      data: [],
      error: message,
    };
  }
}

export async function recordFifaCategoryUsage(
  fifaCategoryId: string
): Promise<{ success: boolean; error?: string }> {
  const trimmedId = fifaCategoryId?.trim();
  if (!trimmedId) {
    return { success: false, error: "Missing FIFA category id." };
  }

  try {
    const response = await fetch(`${apiBase}/fifa-category/public/usage`, {
      method: "POST",
      headers: {
        ...getHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fifa_category_id: trimmedId }),
    });

    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        error: `Failed to record FIFA category usage: ${response.status} ${text}`,
      };
    }

    return { success: true };
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : "Network error while recording FIFA category usage";
    return { success: false, error: message };
  }
}
